import { Environment, EnvironmentEnum, MissingKey } from "@lyfegen-platform/graphql-schema/models";
import i18n from "i18next";
import { useEffect, useState } from "react";
import { initReactI18next } from "react-i18next";
import { useDataQuery } from "../hooks/useDataModule";
import { GET_MISSING_KEYS, GET_TRANSLATIONS } from "../i18n/queries";
import { GetTranslations } from "../i18n/types/GetTranslations";
import LoadingBox from "./LoadingBox";

interface TranslationProps {
	children: React.ReactChild | Array<React.ReactChild>;
	languages: { en: string | null; de: string | null };
	preferredLanguage: string;
	translationsPath: string;
	serverEnvironment: Environment;
}

const Translation: React.FC<TranslationProps> = ({ children, preferredLanguage, languages, translationsPath, serverEnvironment }) => {
	const noTranslate = process.env.REACT_APP_NO_TRANSLATE === "true";
	const supportedLanguages = ["de", "en"];
	const lEnO = !languages.en ? {} : languages.en.length > 0 && JSON.parse(languages.en);
	const ldeO = !languages.de ? {} : languages.de.length > 0 && JSON.parse(languages.de);
	const skip = Boolean(languages.en) && Boolean(languages.de);
	const skipMissingKeys = ![EnvironmentEnum.Development, EnvironmentEnum.Dev, EnvironmentEnum.Stage].includes(serverEnvironment);
	const [en, setEn] = useState(lEnO);
	const [de, setDe] = useState(ldeO);
	const [missingKeys, setMissingKeys] = useState<Array<MissingKey | null>>([]);
	const { loading } = useDataQuery<GetTranslations>(GET_TRANSLATIONS, {
		skip,
		onCompleted: (data) => {
			setStorageAndReturnJSON(data.GetTranslations.en, data.GetTranslations.de);
		},
	});
	useDataQuery(GET_MISSING_KEYS, {
		skip: skipMissingKeys,
		onCompleted: (data: { GetMissingKeys: string }) => {
			const missingKeys: Array<MissingKey | null> = JSON.parse(data.GetMissingKeys);
			setMissingKeys(missingKeys);
		},
		variables: {
			environment: serverEnvironment,
		},
	});
	const setStorageAndReturnJSON = (english: string, german: string) => {
		sessionStorage.setItem("en", english);
		sessionStorage.setItem("de", german);
		setEn(JSON.parse(english));
		setDe(JSON.parse(german));
	};
	i18n.use(initReactI18next).init({
		resources: {
			en: {
				translation: noTranslate ? {} : en,
			},
			de: {
				translation: noTranslate ? {} : de,
			},
		},
		lng: preferredLanguage,
		fallbackLng: supportedLanguages,
		interpolation: {
			escapeValue: false,
		},
		saveMissing: true,
		whitelist: supportedLanguages,
	});

	i18n.on("missingKey", (_, __, key) => {
		// only in these environments for now to avoid overloading production / demo with http post requests
		if (
			[EnvironmentEnum.Development, EnvironmentEnum.Dev, EnvironmentEnum.Stage].includes(serverEnvironment) &&
			key.length > 0 &&
			missingKeys.length &&
			!missingKeys.some(
				(missingKey) =>
					missingKey?.key === key && missingKey.language === i18n.language && missingKey.serverEnvironment === serverEnvironment
			)
		) {
			fetch(translationsPath, { method: "POST", body: JSON.stringify({ key, language: i18n.language, serverEnvironment }) }).catch(
				(error) => console.error(`error in uploading missing translation keys`, error)
			);
		}
	});

	if (preferredLanguage !== "en-US") i18n.changeLanguage(preferredLanguage);

	useEffect(() => {
		i18n.addResourceBundle("en", "translation", noTranslate ? {} : en, undefined, true);
		i18n.addResourceBundle("de", "translation", noTranslate ? {} : de, undefined, true);
	}, [en, de]);

	if (loading) return <LoadingBox fullHeight />;

	return <>{children}</>;
};

export default Translation;
